/**
 * Insights — design tokens.
 *
 * Semantic remap over AppColors so the landing stays visually native to the Myma app
 * while the AI narrative gets its own orange accent derived from Myma's secondary color.
 *
 * Rules:
 * - No foreign palette. All colors trace back to AppColors (`themes/colors.js`).
 * - No Arial Narrow. Typography inherits Roboto from the MUI theme.
 * - Numerics rely on `font-variant-numeric: tabular-nums` for column alignment.
 */

import AppColors from '../../themes/colors';

export const IC = {
  // Surfaces
  bg: AppColors.background.default,           // #f8f9fa — page
  surface: AppColors.background.paper,         // #ffffff — cards
  surfaceAlt: '#fafaf9',                       // subtle zebra
  dark: AppColors.primary.main,                // #28193e — sidebar/Inaction dark card
  darkElev: AppColors.primary.light,           // #3d2a57 — nested box on dark card

  // Chrome (borders, dividers)
  bdr: AppColors.ui.border,                    // rgba(0,0,0,.08)
  bdrStrong: 'rgba(0, 0, 0, 0.12)',
  bdrL: 'rgba(0, 0, 0, 0.05)',

  // Text
  tx: AppColors.text.primary,                  // #222222
  tx2: AppColors.text.secondary,               // #666666
  txM: AppColors.text.hint,                    // #888888
  onDark: AppColors.text.contrast,             // #ffffff

  // Brand
  pri: AppColors.primary.main,                 // #28193e — structural chrome
  priSoft: 'rgba(40, 25, 62, 0.08)',
  // AI / Forecast / "look here"
  ai: AppColors.secondary.main,                // #EA572E — Myma orange
  aiSoft: 'rgba(234, 87, 46, 0.10)',
  aiStrong: AppColors.secondary.dark,          // #c73e1d

  // Status (mapped to Myma's softer palette)
  grn: AppColors.status.success,               // #8dc572
  grnSoft: 'rgba(141, 197, 114, 0.12)',
  red: AppColors.status.error,                 // #be6464
  redSoft: 'rgba(190, 100, 100, 0.12)',
  amb: AppColors.status.warning,               // #f0ad4e
  ambSoft: 'rgba(240, 173, 78, 0.14)',
  blu: AppColors.status.info,                  // #337ab7
  bluSoft: 'rgba(51, 122, 183, 0.10)',

  // Data viz
  chartA: AppColors.chart.purple,              // #8b66c2 — primary data ink
  chartB: AppColors.chart.blue,                // #5b8ef2
  chartC: AppColors.chart.green,               // #69c070
  chartD: AppColors.chart.orange,              // #f5a742
  chartE: AppColors.chart.teal,                // #5cbcb8
  chartF: AppColors.chart.red,                 // #e35d5d
};

// Typography scale — 5 steps, maps to MUI variants where possible.
export const IFontSizes = {
  xs: 11,   // caption / meta
  sm: 12,   // body2 / default
  md: 14,   // body1 / card titles
  lg: 18,   // subtitle1 / KPI values
  xl: 24,   // h4 / section numerals
  hero: 44, // hero numeric — dominant climax on first fold
};

// Font weights kept disciplined: 400 body / 500 labels / 700 deltas.
export const IFontWeights = {
  body: 400,
  label: 500,
  strong: 700,
};

// Tight line-height for feeds, airy for prose.
export const ILineHeight = {
  feed: 1.3,
  prose: 1.45,
};

export const IRadius = {
  card: 12, // matches MUI Paper outlined convention in the rest of the app
  pill: 8,
  chip: 4,
};

// tabular-nums + slight negative letter-spacing for big numerals, used via sx merge.
export const INumericSx = {
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.01em',
};

export const IHeroNumericSx = {
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.02em',
};
